import { Globe } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { money } from "@/lib/api";

/** A converted "≈" line shown beside a real amount. Never used for billing. */
export const Fx = ({ amount, from = "AED", className = "", testId }) => {
  const fx = useCurrency();
  if (!fx || fx.isBase || amount == null) return null;
  const converted = fx.convert(amount, from);
  if (converted == null) return null;
  return (
    <span data-testid={testId} className={`whitespace-nowrap text-[#667788] ${className}`}>
      ≈ {fx.format(converted)}
    </span>
  );
};

/** The real amount plus its converted estimate underneath. */
export const Amount = ({ value, currency = "AED", className = "", testId, block = false }) => (
  <span className={className} data-testid={testId}>
    {money(value, currency)}
    {block ? (
      <Fx amount={value} from={currency} className="mt-0.5 block text-xs" />
    ) : (
      <Fx amount={value} from={currency} className="ml-1.5 text-xs" />
    )}
  </span>
);

export const CurrencyPicker = ({ compact = false }) => {
  const fx = useCurrency();
  if (!fx || !fx.currencies.length) return null;
  return (
    <label className="flex items-center gap-1.5" title="Change the display currency">
      <Globe className="h-4 w-4 shrink-0 text-[#8598ac]" aria-hidden />
      <span className="sr-only">Display currency</span>
      <select
        data-testid="currency-picker"
        value={fx.code}
        onChange={(e) => fx.setCurrency(e.target.value)}
        className={`cursor-pointer rounded-[9px] border border-[#DFE6ED] bg-white font-semibold text-[#102A43] outline-none transition-colors hover:bg-[#eef2f7] focus:border-[#147D78] ${
          compact ? "px-2 py-1.5 text-xs" : "px-2.5 py-2 text-sm"
        }`}
      >
        {fx.currencies.map((c) => (
          <option key={c.code} value={c.code}>
            {c.code}
            {c.code === "AED" ? " (billing)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
};

/** One-line disclaimer to sit under converted prices. */
export const FxNote = ({ className = "" }) => {
  const fx = useCurrency();
  if (!fx || fx.isBase) return null;
  return (
    <p data-testid="fx-note" className={`text-xs text-[#667788] ${className}`}>
      Converted from AED for guidance only at today&apos;s indicative rate
      {fx.asOf ? ` (${String(fx.asOf).slice(0, 10)})` : ""}
      {fx.stale ? ", using an older cached rate" : ""}. All subscriptions are charged in AED.
    </p>
  );
};
